<template>
  <v-card
    :outlined="share"
    :class="{ 'mx-3': share, 'py-0': true }"
    style="position: relative; max-width: 512px;"
  >
    <!-- Meta --->
    <div v-if="status.meta && !share">
      <FbText :text="status.meta" class="px-4 py-2 ma-0 trim-oneline" />
      <v-divider></v-divider>
    </div>
    <!-- Author, timestamp, caption --->
    <div class="ma-0 px-4 pt-3">
      <v-layout row class="ma-0 mb-2" align-center>
        <v-avatar size="50">
          <img class="primary" :src="profilePicture">
        </v-avatar>
        <v-layout column class="ml-3">
          <FbText :text="title" class="ma-0 trim-oneline" />
          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <p class="text--secondary ma-0 body-2 trim-oneline" v-on="on" v-bind="attrs">
                {{ subtitle }}
              </p>
            </template>
            <v-list>
              <v-list-item
                v-for="(item, index) in items"
                :key="index"
              >
                <v-list-item-title @click="item.action">{{ item.title }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-layout>
      </v-layout>
      <FbText
        v-if="status.caption"
        :text="status.caption"
        :max-lines="8"
        class="mb-3 mx-0"
      >
        {{ status.caption }}
      </FbText>
    </div>
    <!-- Content, if any -->
    <v-content
      v-if="hasContent"
      :class="{ 
        'pa-0': true, 
        [status.content.type === 'share' ? 'mb-3' : 'ma-0']: true 
      }"
    >
      <img
        v-if="status.content.type ==='picture'"
        class="primary ma-0 pa-0"
        style="display: block; width: 100%; max-height: 512px; object-fit: cover;"
        :src="status.content.src" />
      <FbWebLink
        v-else-if="status.content.type === 'link'"
        class="mx-4 mb-2"
        :url="status.content.url"
        :cta="status.content.cta" />
      <FbStatus
        v-else-if="status.content.type === 'share'"
        :status="Store.fb.posts[status.content.id]"
        mode="share" />
    </v-content>
    <!-- Stats -->
    <v-layout row wrap 
      v-if="!share && stats.length"
      class="ma-0 px-4 mt-2 mb-3"
    >
      <p
        v-for="(stat, i) in stats"
        :key="i"
        class="text--secondary font-weight-bold body-2 mr-6 ma-0"
      >
        {{ stat }}
      </p>
    </v-layout>
    <!-- Action buttons -->
    <v-row v-if="!share" no-gutters >
      <v-col
        v-for="btn in actionBtns"
        :key="btn.name"
      >
        <v-btn
          tile depressed block
          color="grey lighten-3"
          :class="{
            'grey--text': !btn.active,
            'text--darken-1': !btn.active,
            'primary--text': btn.active
          }"
          @click="btn.click"
        >
          <v-icon left :size="btn.iconSize">{{ btn.icon }}</v-icon>
          {{ btn.name }}
        </v-btn>
      </v-col>
    </v-row>
    <!-- Right side options (list mode) -->
    <v-layout
      column
      v-if="mode === 'list'"
      style="position: absolute; right: 0; top: 0; margin-right: -60px;"
    >
      <v-btn icon fab dark small class="primary mt-2" @click="$router.push(`/fb-posts/${id}`)">
        <v-icon>mdi-pencil</v-icon>
      </v-btn>
      <v-btn icon fab dark small class="primary mt-2" @click="$emit('delete', id)">
        <v-icon>mdi-delete</v-icon>
      </v-btn>
    </v-layout>
  </v-card>
</template>

<script>
import moment from 'moment'

import FbText from './FbText'
import FbWebLink from './FbWebLink'

import FbMemberMixin from '../mixins/fb-member'
import { pluralize } from '../utils'

export default {
  name: 'FbStatus',
  mixins: [FbMemberMixin],
  components: {
    FbText,
    FbWebLink
  },
  props: {
    id: String,
    mode: String,
    status: Object
  },
  data() {
    return {
      maxCaptionLines: 8,
      items: [
        { title: 'Copy', action: this.copyTimestamp },
        { title: 'Paste', action: this.pasteTimestamp },
      ],
    }
  },
  computed: {
    likeCount() {
      return (this.status.likeCount || 0) 
        + +(this.status.likeable && this.status.liked)
    },
    commentCount() {
      return Math.max(
        this.status.commentCount || 0, 
        Object.keys(this.status.comments || {}).length
      )
    },
    shareCount() {
      return (this.status.shareCount || 0) 
        + +(this.status.shareable && this.status.shared)
    },
    profilePicture() {
      if (this.status.author == null)
        return ''
      const author = this.status.author
      const result = this.memberFromRef(author)
      return result == null ? '' : result.member.profilePicture
    },
    share() {
      return this.mode === 'share'
    },
    hasContent() {
      if (this.status.content && this.status.content.type)
        return !this.share || this.status.content.type !== 'share'
      return false
    },
    title() {
      return `${this.status.author || '(no author)'} ${this.status.action || ''}`.trim()
    },
    subtitle() {
      return this.status.sponsored 
        ? 'Sponsored' 
        : moment.utc(this.status.timestamp).format('D MMM YYYY [at] h:mm a')
    },
    stats() {
      const shared = {
        includeN: true,
        blankIfZero: true
      }
      return [
        pluralize({
          n: this.likeCount,
          singular: 'like',
          ...shared
        }),
        pluralize({
          n: this.commentCount,
          singular: 'comment',
          ...shared
        }),
        pluralize({
          n: this.shareCount,
          singular: 'share',
          ...shared
        })
      ].filter(s => s.length > 0)
    },
    actionBtns() {
      return [
        { 
          name: 'Like', 
          icon: 'mdi-thumb-up', 
          disabled: !this.status.likeable,
          active: this.status.liked,
          click: this.onLike
        },
        {
          name: 'Comment',
          icon: 'mdi-comment',
          click: () => 0
        },
        {
          name: 'Share',
          icon: 'mdi-share',
          iconSize: 26,
          disabled: !this.status.shareable,
          active: this.status.shared,
          click: this.onShare
        }
      ].filter(e => !e.disabled)
    }
  },
  methods: {
    copyTimestamp() {
      this.$set(this.Store, 'timestampClipboard', this.status.timestamp)
    },
    pasteTimestamp() {
      const status = this.Store.fb.posts[this.id]
      this.$set(status, 'timestamp', this.Store.timestampClipboard)
    },
    onLike() {
      this._flipProp('liked')
    },
    onShare() {
      if (this.mode === 'list')
        this.$emit('share', this.id)
      else
        this._flipProp('shared')
    },
    _flipProp(prop) {
      const status = this.Store.fb.posts[this.id]
      this.$set(status, prop, !this.status[prop])
    }
  }
}
</script>